/**
 * Sync Cliente Neto en pedidos: apply por SKU, re-sync si cambia IVA del producto, sin doble apply.
 * En view/edit (o create precargado desde cotización) no re-aplica CN al lote inicial:
 * los precios ya vienen con IVA embebido desde el API.
 */
import { useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react'
import type { OrderData, LineItem } from '../../types/orderFormTypes'
import {
  isClienteNetoActive,
  lineItemSku,
  createClienteNetoAppliedRegistry,
  clearClienteNetoAppliedRegistry,
  markClienteNetoAppliedForLines,
  unmarkClienteNetoAppliedSku,
  getSkusNeedingClienteNetoSync,
  applyClienteNetoSyncToLineItems,
  recalculateOrderTotalWithClienteNeto,
  type ClienteNetoAppliedRegistry
} from '../../utils/clienteNetoOrder'

interface UseClienteNetoProductSyncProps {
  mode: 'view' | 'edit' | 'create'
  formData: OrderData
  productDetails: { [key: string]: unknown }
  costoEnvio: string
  setFormData: Dispatch<SetStateAction<OrderData>>
}

export interface UseClienteNetoProductSyncReturn {
  markLineItemsCnApplied: (items: LineItem[]) => void
  unmarkLineItemCnApplied: (sku: string) => void
  clearCnAppliedSkus: () => void
}

export function useClienteNetoProductSync({
  mode,
  formData,
  productDetails,
  costoEnvio,
  setFormData
}: UseClienteNetoProductSyncProps): UseClienteNetoProductSyncReturn {
  const registryRef = useRef<ClienteNetoAppliedRegistry>(createClienteNetoAppliedRegistry())
  // view/edit: precios ya vienen con CN del API.
  // create con líneas precargadas (p. ej. convertir cotización): precios ya vienen con CN.
  const skipInitialBatchRef = useRef(
    mode === 'view' ||
    mode === 'edit' ||
      (mode === 'create' &&
        isClienteNetoActive(formData.extraDetails) &&
        (formData.lineItems?.length ?? 0) > 0)
  )

  const markLineItemsCnApplied = useCallback(
    (items: LineItem[]) => {
      markClienteNetoAppliedForLines(registryRef.current, items, productDetails)
    },
    [productDetails]
  )

  const unmarkLineItemCnApplied = useCallback((sku: string) => {
    unmarkClienteNetoAppliedSku(registryRef.current, sku)
  }, [])

  const clearCnAppliedSkus = useCallback(() => {
    clearClienteNetoAppliedRegistry(registryRef.current)
  }, [])

  const clienteNetoActive = isClienteNetoActive(formData.extraDetails)

  useEffect(() => {
    // Visualizar: precios y líneas vienen del pedido guardado, no recalcular con catálogo.
    if (mode === 'view') return

    if (!clienteNetoActive) {
      clearCnAppliedSkus()
      skipInitialBatchRef.current = mode === 'edit'
      return
    }

    const lines = formData.lineItems
    if (!lines?.length) return

    if (skipInitialBatchRef.current) {
      markLineItemsCnApplied(lines)
      skipInitialBatchRef.current = false
      return
    }

    const { newSkus, reapplySkus } = getSkusNeedingClienteNetoSync(
      lines,
      productDetails,
      registryRef.current
    )

    if (newSkus.length === 0 && reapplySkus.length === 0) return

    const skusToTouch: string[] = Array.from(new Set([...newSkus, ...reapplySkus]))

    setFormData((prev) => {
      if (!isClienteNetoActive(prev.extraDetails)) return prev
      const prevLines = prev.lineItems || []
      const synced = applyClienteNetoSyncToLineItems(
        [...prevLines],
        productDetails,
        newSkus,
        reapplySkus
      )

      const touchedLines = synced.filter((row) => skusToTouch.includes(lineItemSku(row)))
      markClienteNetoAppliedForLines(registryRef.current, touchedLines, productDetails)

      const pricesChanged = synced.some(
        (row, i) => row.price !== prevLines[i]?.price
      )
      if (!pricesChanged) return prev

      const total = recalculateOrderTotalWithClienteNeto(
        synced,
        productDetails,
        costoEnvio,
        true
      )
      return { ...prev, lineItems: synced, total }
    })
  }, [
    clienteNetoActive,
    formData.lineItems,
    productDetails,
    costoEnvio,
    mode,
    setFormData,
    clearCnAppliedSkus,
    markLineItemsCnApplied
  ])

  return { markLineItemsCnApplied, unmarkLineItemCnApplied, clearCnAppliedSkus }
}
